import { z } from 'zod';
import {
  ANALYTICS_CORE_FIXTURE_ID,
  AUTO_ANALYZER_MAX_GAMES,
  MAX_PROFIT_MATRIX_AXIS,
  MAX_PROFIT_MATRIX_CELLS,
} from './constants.ts';

/** Target M: any reasonable decimal (not a 2/5/10 enum). Typical crash points are >= 1. */
export const targetMultiplierSchema = z
  .number({
    required_error: 'Target multiplier is required',
    invalid_type_error: 'Target multiplier must be a finite number',
  })
  .finite()
  .gte(1, 'Target multiplier must be at least 1.00')
  .lte(1_000_000, 'Target multiplier is unreasonably large');

export const recoveryStatusSchema = z.enum(['CONFIRMED', 'RECOVERED', 'UNCERTAIN']);

export const dataInclusionSchema = z
  .object({
    confirmed: z.boolean(),
    recovered: z.boolean(),
    uncertain: z.boolean(),
  })
  .refine((value) => value.confirmed || value.recovered || value.uncertain, {
    message: 'Select at least one of CONFIRMED, RECOVERED, or UNCERTAIN',
  });

export const periodPresetSchema = z.enum(['all', 'last_24h', 'last_7d', 'last_30d', 'custom']);

const optionalPositiveInt = z.number().int().positive().max(10_000_000).nullable();
const optionalMoney = z.number().finite().gt(0).max(1_000_000_000).nullable();
const optionalHours = z.number().finite().gt(0).max(24 * 365 * 20).nullable();
const optionalUtc = z.string().datetime().nullable().optional();

export const analysisQuerySchema = z
  .object({
    targetMultiplier: targetMultiplierSchema,
    period: periodPresetSchema,
    fromUtc: z.string().datetime().nullable(),
    toUtc: z.string().datetime().nullable(),
    inclusion: dataInclusionSchema,
    rollingWindowHours: optionalHours,
    roundWindow: optionalPositiveInt,
    droughtLength: optionalPositiveInt,
    entryDrought: optionalPositiveInt,
    exitDrought: optionalPositiveInt,
    futureRounds: optionalPositiveInt,
    stake: optionalMoney,
  })
  .superRefine((query, ctx) => {
    if (query.period === 'custom') {
      if (!query.fromUtc || !query.toUtc) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Custom period requires both From and To timestamps',
          path: ['fromUtc'],
        });
      }
    }
    if (query.fromUtc && query.toUtc && query.fromUtc > query.toUtc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'From must be earlier than or equal to To',
        path: ['fromUtc'],
      });
    }
  });

export const defaultAnalysisQuery = {
  targetMultiplier: 2,
  period: 'all' as const,
  fromUtc: null,
  toUtc: null,
  inclusion: { confirmed: true, recovered: true, uncertain: true },
  rollingWindowHours: null,
  roundWindow: null,
  droughtLength: null,
  entryDrought: null,
  exitDrought: null,
  futureRounds: null,
  stake: null,
};

export const defaultInclusion = { confirmed: true, recovered: true, uncertain: true } as const;

export const exploreRequestSchema = z.object({
  fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
  targetMultiplier: targetMultiplierSchema,
  fromUtc: optionalUtc,
  toUtc: optionalUtc,
  inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
  atLeastDroughtLength: z.number().int().positive().max(10_000_000).default(1),
});

export const simulateRequestSchema = z.object({
  fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
  targetMultiplier: targetMultiplierSchema,
  entryDrought: z.number().int().positive().max(10_000_000),
  stopDrought: z.number().int().positive().max(10_000_000),
  stake: z.number().finite().gt(0).max(1_000_000_000),
  fromUtc: optionalUtc,
  toUtc: optionalUtc,
  inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
});

export const droughtAnalyticsRequestSchema = z.object({
  fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
  selectedTarget: targetMultiplierSchema.default(2),
  fromUtc: optionalUtc,
  toUtc: optionalUtc,
  inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
});

export const stakingModeSchema = z.enum(['FIXED', 'RECOVERY', 'MARTINGALE']);

export const profitCalculateRequestSchema = z
  .object({
    fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
    targetMultiplier: targetMultiplierSchema,
    entryDrought: z.number().int().positive().max(10_000_000),
    stopDrought: z.number().int().positive().max(10_000_000),
    stakingMode: stakingModeSchema,
    baseStake: z.number().finite().gt(0).max(1_000_000_000).default(1),
    targetEpisodeProfit: z.number().finite().gt(0).max(1_000_000_000).default(1),
    fromUtc: optionalUtc,
    toUtc: optionalUtc,
    inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
  })
  .superRefine((query, ctx) => {
    if (query.stopDrought < query.entryDrought + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Stop S must be an integer ≥ D + 1.',
        path: ['stopDrought'],
      });
    }
    if (query.stakingMode === 'MARTINGALE' && query.targetMultiplier !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'MARTINGALE is available only for target 2x.',
        path: ['stakingMode'],
      });
    }
  });

export const profitMatrixRequestSchema = z
  .object({
    fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
    targetMultiplier: targetMultiplierSchema.default(2),
    stakingMode: z.enum(['FIXED', 'MARTINGALE']).default('FIXED'),
    baseStake: z.number().finite().gt(0).max(1_000_000_000).default(1),
    entryFrom: z.number().int().min(1).max(MAX_PROFIT_MATRIX_AXIS).default(2),
    entryTo: z.number().int().min(1).max(MAX_PROFIT_MATRIX_AXIS).default(10),
    stopFrom: z.number().int().min(1).max(MAX_PROFIT_MATRIX_AXIS).default(4),
    stopTo: z.number().int().min(1).max(MAX_PROFIT_MATRIX_AXIS).default(21),
    fromUtc: optionalUtc,
    toUtc: optionalUtc,
    inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
  })
  .superRefine((query, ctx) => {
    if (query.entryTo < query.entryFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Entry drought range must have from ≤ to.',
        path: ['entryTo'],
      });
    }
    if (query.stopTo < query.stopFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Stop drought range must have from ≤ to.',
        path: ['stopTo'],
      });
    }
    if (query.stakingMode === 'MARTINGALE' && query.targetMultiplier !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'MARTINGALE is available only for target 2x.',
        path: ['stakingMode'],
      });
    }
    const requestedCells = (query.entryTo - query.entryFrom + 1) * (query.stopTo - query.stopFrom + 1);
    if (requestedCells > MAX_PROFIT_MATRIX_CELLS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Requested matrix has ${requestedCells.toLocaleString('en-US')} cells (limit ${MAX_PROFIT_MATRIX_CELLS.toLocaleString('en-US')}). Narrow the Entry D and Stop S ranges. The requested range was not truncated.`,
        path: ['stopTo'],
      });
    }
  });

export const memoryAnalyticsRequestSchema = z
  .object({
    fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
    fromUtc: optionalUtc,
    toUtc: optionalUtc,
    inclusion: dataInclusionSchema.default({ ...defaultInclusion }),
    stateCuts: z.array(z.number().finite().positive()).min(1).max(12).default([1.2, 2, 5, 10, 50, 100]),
    binaryTarget: targetMultiplierSchema.default(2),
    patternLengths: z.array(z.number().int().min(2).max(5)).min(1).max(4).default([2, 3, 4]),
    minPatternSample: z.number().int().positive().max(10_000).default(30),
    minEffectSize: z.number().finite().min(0).max(1).default(0.05),
    fdrQ: z.number().finite().gt(0).lte(1).default(0.05),
    patternTarget: targetMultiplierSchema.default(2),
    includeHoldout: z.boolean().default(false),
    permutationCount: z.number().int().min(0).max(1000).default(1000),
    permutationSeed: z.number().int().default(20_260_918),
    trimmedMeanFraction: z.number().finite().gt(0).lt(0.5).default(0.1),
  })
  .superRefine((query, ctx) => {
    for (let i = 1; i < query.stateCuts.length; i += 1) {
      const prev = query.stateCuts[i - 1];
      const next = query.stateCuts[i];
      if (prev === undefined || next === undefined || !(next > prev)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State cuts must be strictly increasing',
          path: ['stateCuts', i],
        });
      }
    }
  });

export { AUTO_ANALYZER_MAX_GAMES };

export const autoAnalyzerRequestSchema = z.object({
  fixtureId: z.string().min(1).default(ANALYTICS_CORE_FIXTURE_ID),
  targetMultiplier: targetMultiplierSchema.refine((value) => value >= 2, {
    message: 'Analyzer target must be at least 2x.',
  }),
  maxGames: z
    .number({
      required_error: 'Maximum games is required',
      invalid_type_error: 'Maximum games must be an integer',
    })
    .int()
    .min(1)
    .max(AUTO_ANALYZER_MAX_GAMES),
});

export const paperStrategyConfigSchema = z.object({
  id: z.string().min(1).max(80),
  targetMultiplier: targetMultiplierSchema,
  entryDrought: z.number().int().positive().max(MAX_PROFIT_MATRIX_AXIS),
  stopDrought: z.number().int().positive().max(MAX_PROFIT_MATRIX_AXIS),
  stakingMode: z.enum(['FIXED', 'MARTINGALE']),
  baseStake: z.number().finite().positive().max(1_000_000),
});

export const paperSimulationCreateSchema = z.object({
  startingBalance: z.number().finite().positive().max(1_000_000_000).default(500),
  startSequence: z.number().int().positive().optional(),
});

export const multiplierExplorerRequestSchema = z
  .object({
    threshold: z.number().finite().gte(0).lte(1_000_000).optional(),
    topN: z.number().int().positive().max(10_000).optional(),
    offset: z.number().int().min(0).max(10_000_000).optional(),
    limit: z.number().int().positive().max(500).optional(),
    contextSequence: z.number().int().positive().optional(),
    contextBefore: z.number().int().min(0).max(200).optional(),
    contextAfter: z.number().int().min(0).max(200).optional(),
  })
  .refine((value) => value.threshold != null || value.topN != null, {
    message: 'Provide a multiplier threshold or a Top N count.',
  });

export function parseTargetMultiplierInput(raw: string): number {
  const normalized = raw.trim().replace(/x$/i, '');
  const value = Number(normalized);
  return targetMultiplierSchema.parse(value);
}
